/**
 * Hyperliquid EIP-712 Signing
 *
 * Uses @nktkas/hyperliquid SDK for correct L1 action signing.
 * L1 actions (orders, cancels, leverage) use:
 *   - keccak-256 of MessagePack-encoded action + binary nonce
 *   - EIP-712 domain: { name: "Exchange", chainId: 1337 }
 *   - primaryType: "Agent" with { source, connectionId }
 *
 * The API wallet (agent) signs orders on behalf of the main wallet.
 */

import { signL1Action, actionSorter } from "@nktkas/hyperliquid/signing";
import type { Signature } from "@nktkas/hyperliquid/signing";
import type { PrivateKeyAccount } from "viem/accounts";
import type { HLSignature } from "./types";

// ============================================================================
// L1 Action Signing (orders, cancels, leverage)
// ============================================================================

/**
 * Sign an L1 action using the phantom agent pattern via the SDK.
 * The SDK handles keccak-256 hashing of msgpack-encoded action + nonce,
 * and signs the resulting connectionId with the correct EIP-712 domain.
 *
 * @param account - viem PrivateKeyAccount (API wallet)
 * @param action - The action to sign (order, cancel, etc.)
 * @param nonce - Timestamp nonce
 * @param isMainnet - Whether signing for mainnet or testnet
 */
export async function signPhantomAgent(
  account: PrivateKeyAccount,
  action: Record<string, unknown>,
  nonce: number,
  isMainnet = true,
): Promise<HLSignature> {
  const signature = await signL1Action({
    wallet: account,
    action,
    nonce,
    isTestnet: !isMainnet,
  });

  return signature;
}

// ============================================================================
// Action Sorting
// ============================================================================

/**
 * Sort an order action's keys to match the canonical order expected by the SDK.
 * Key order matters because MessagePack serialization is order-dependent.
 */
export function sortOrderAction(
  action: Record<string, unknown>,
): Record<string, unknown> {
  return actionSorter.order(action as Parameters<typeof actionSorter.order>[0]);
}

/**
 * Sort a cancel action's keys to match the canonical order.
 */
export function sortCancelAction(
  action: Record<string, unknown>,
): Record<string, unknown> {
  return actionSorter.cancel(
    action as Parameters<typeof actionSorter.cancel>[0],
  );
}

/**
 * Sort an updateLeverage action's keys to match the canonical order.
 */
export function sortUpdateLeverageAction(
  action: Record<string, unknown>,
): Record<string, unknown> {
  return actionSorter.updateLeverage(
    action as Parameters<typeof actionSorter.updateLeverage>[0],
  );
}

// ============================================================================
// Signature Utilities
// ============================================================================

/**
 * Split a hex signature string into r, s, v components.
 */
export function splitSignature(signature: `0x${string}`): HLSignature {
  const sig = signature.slice(2);
  return {
    r: `0x${sig.slice(0, 64)}`,
    s: `0x${sig.slice(64, 128)}`,
    v: parseInt(sig.slice(128, 130), 16),
  };
}

/**
 * Generate a random client order ID with a prefix.
 * Format: 0x{prefix_hex}{random_hex} (16 bytes total)
 */
export function generateClientOrderId(prefix = "perp"): `0x${string}` {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  for (let i = 0; i < prefix.length && i < bytes.length; i++) {
    bytes[i] = prefix.charCodeAt(i) & 0xff;
  }
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return `0x${hex}`;
}

export type { Signature };
